<template>
  <b-modal
    ref="modal"
    hide-footer
    centered
    title="Add Kanban Widget"
    @shown="modalShown = true"
    @hidden="modalShown = false"
  >
    <b-form @submit.prevent="save">
      <b-form-group
        :label="createLanesGrid ? 'Lanes storage title:' : 'Lanes storage:'"
      >
        <div v-if="!createLanesGrid">
          <b-form-select
            v-model="$v.lanesStorage.$model"
            :options="availableDataGrids"
            :class="{ 'is-invalid': $v.lanesStorage.$error }"
            :disabled="inProcess"
          />
        </div>
        <b-form-input
          v-else
          v-model="$v.lanesStorageTitle.$model"
          :class="{ 'is-invalid': $v.lanesStorageTitle.$error }"
          ref="lanesStorageTitleInput"
        />
        <b-form-invalid-feedback
          v-if="!$v.lanesStorage.required"
        >
          Lanes storage is required
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.lanesStorageTitle.required"
        >
          Lanes storage title is required
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.lanesStorageTitle.isUnique"
        >
          Table with the same name already exists
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.lanesStorageTitle.alphaNum"
        >
          Table title accepts only alphanumerics and can not start with number or dash sign
        </b-form-invalid-feedback>
        <b-form-checkbox
          v-model="createLanesGrid"
        >
          Create one for me
        </b-form-checkbox>
      </b-form-group>
      <div v-if="lanesStorage && !createLanesGrid">
        <b-form-group
          label="Lane column:"
        >
          <b-form-select
            v-model.trim="$v.laneCol.$model"
            :options="availableLanesStorageColumns"
            :class="{ 'is-invalid': $v.laneCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.laneCol.required"
          >
            Lane column is required
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
      <b-form-group
        :label="createItemsGrid ? 'Items storage title:' : 'Items storage:'"
      >
        <div v-if="!createItemsGrid">
          <b-form-select
            v-model.trim="$v.itemsStorage.$model"
            :options="availableDataGrids"
            :class="{ 'is-invalid': $v.itemsStorage.$error }"
            :disabled="inProcess"
          />
        </div>
        <b-form-input
          v-else
          v-model="$v.itemsStorageTitle.$model"
          :class="{ 'is-invalid': $v.itemsStorageTitle.$error }"
          ref="itemsStorageTitleInput"
        />
        <b-form-invalid-feedback
          v-if="!$v.itemsStorage.required"
        >
          Items storage is required
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.itemsStorageTitle.required"
        >
          Items storage title is required
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.itemsStorageTitle.isUnique"
        >
          Table with the same name already exists
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.itemsStorageTitle.alphaNum"
        >
          Table title accepts only alphanumerics and can not start with number or dash sign
        </b-form-invalid-feedback>
        <b-form-checkbox
          v-model="createItemsGrid"
        >
          Create one for me
        </b-form-checkbox>
      </b-form-group>
      <div v-if="itemsStorage && !createItemsGrid">
        <b-form-group
          label="Title column:"
        >
          <b-form-select
            v-model.trim="$v.titleCol.$model"
            :options="availableItemsStorageColumns('titleCol')"
            :class="{ 'is-invalid': $v.titleCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.titleCol.required"
          >
            Title column is required
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          label="Status column:"
        >
          <b-form-select
            v-model.trim="$v.statusCol.$model"
            :options="availableItemsStorageColumns('statusCol')"
            :class="{ 'is-invalid': $v.statusCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.statusCol.required"
          >
            Status column is required
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          label="Description column:"
        >
          <b-form-select
            v-model.trim="$v.descriptionCol.$model"
            :options="availableItemsStorageColumns('descriptionCol')"
            :class="{ 'is-invalid': $v.descriptionCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.descriptionCol.required"
          >
            Description column is required
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          label="Sort index column:"
        >
          <b-form-select
            v-model.trim="$v.sortIndexCol.$model"
            :options="availableItemsStorageColumns('sortIndexCol')"
            :class="{ 'is-invalid': $v.sortIndexCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.sortIndexCol.required"
          >
            Sort index column is required
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
      <b-form-group>
        <template slot="label">
          <span>Template code:</span>
          <icon
            name="question-circle"
            style="vertical-align: -3px;"
            v-b-tooltip="'Use `{{ item }}` variable to access data'"
          />
        </template>

        <div class="templateEditor">
          <codemirror
            v-if="modalShown"
            :value="template"
            :options="cmTemplateOptions"
            @input="onCmTemplateCodeChange"
            @keydown.ctrl.83.native.prevent="save"
          />
        </div>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
          v-model="addOnChangeScript"
        >
          add on change script
        </b-form-checkbox>
      </b-form-group>
      <b-form-group
        v-if="addOnChangeScript"
        label="On change code:"
      >
        <div class="templateEditor">
          <codemirror
            v-if="modalShown"
            :value="onChangeScript"
            :options="cmScriptOptions"
            @input="onCmScriptCodeChange"
            @keydown.ctrl.83.native.prevent="save"
          />
        </div>
      </b-form-group>
      <div>
        <b-button
          variant="success"
          type="submit"
          :disabled="inProcess"
        >
          Save
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import { required, requiredUnless, requiredIf, helpers } from 'vuelidate/lib/validators'
const alphaNum = helpers.regex('alphaNum', /^[A-Za-z0-9_-\s]*$/)
// services
import db from '@/services/db'
// helpers
import constants from '@/helpers/constants'
import eventBus from '@/helpers/event-bus'

export default {
  props: ['gsOptions'],
  data () {
    return {
      modalShown: false,
      inProcess: false,
      lanesStorage: null,
      laneCol: null,
      itemsStorage: null,
      titleCol: null,
      statusCol: null,
      descriptionCol: null,
      sortIndexCol: null,
      template: '',
      addOnChangeScript: false,
      onChangeScript: '',
      cmTemplateOptions: constants.cmTemplateOptions,
      cmScriptOptions: constants.cmScriptOptions,
      createLanesGrid: false,
      lanesStorageTitle: null,
      createItemsGrid: false,
      itemsStorageTitle: null
    }
  },
  watch: {
    lanesStorage () {
      this.laneCol = null
    },
    itemsStorage () {
      this.titleCol = null
      this.statusCol = null
      this.descriptionCol = null
      this.sortIndexCol = null
    },
    createLanesGrid (val) {
      if (!val) return
      this.lanesStorage = null
      this.laneCol = null

      setTimeout(() => {
        this.$refs.lanesStorageTitleInput.focus()
      }, 200)
    },
    createItemsGrid (val) {
      if (!val) return
      this.itemsStorage = null

      setTimeout(() => {
        this.$refs.itemsStorageTitleInput.focus()
      }, 200)
    }
  },
  computed: {
    ...mapState([
      'dataGrid_options',
      'currentTab',
      'currentSubTab'
    ]),
    availableDataGrids () {
      const dataGrids = []

      for (let gridId in this.dataGrid_options) {
        if (!this.dataGrid_options[gridId]) {
          continue
        }
        dataGrids.push({ value: gridId, text: this.dataGrid_options[gridId].title })
      }

      return dataGrids
    },
    availableLanesStorageColumns () {
      const columns = []

      const meta = this.dataGrid_options[this.lanesStorage].meta

      for (let colKey in meta) {
        const item = meta[colKey]

        columns.push({ text: item.displayName, value: colKey })
      }

      return columns
    },
    isLanesGridRequired () {
      return this.createLanesGrid
    },
    isItemsGridRequired () {
      return this.createItemsGrid
    }
  },
  methods: {
    availableItemsStorageColumns (currentCol) {
      const columns = [
        { text: 'NOT SELECTED', value: null }
      ]

      const meta = this.dataGrid_options[this.itemsStorage].meta

      for (let colKey in meta) {
        const item = meta[colKey]

        switch (colKey) {
          case this.titleCol: if (currentCol !== 'titleCol') continue
            break
          case this.statusCol: if (currentCol !== 'statusCol') continue
            break
          case this.descriptionCol: if (currentCol !== 'descriptionCol') continue
            break
          case this.sortIndexCol: if (currentCol !== 'sortIndexCol') continue
            break
        }

        columns.push({ text: item.displayName, value: colKey })
      }

      return columns
    },
    open () {
      this.$refs.modal.show()
    },
    close () {
      this.$refs.modal.hide()
    },
    onCmTemplateCodeChange (code) {
      this.template = code
    },
    onCmScriptCodeChange (code) {
      this.onChangeScript = code
    },
    async doCreateLanesGrid () {
      const gridId = this.$sanitize(this.lanesStorageTitle)
      const gridData = {
        title: this.lanesStorageTitle,
        storageType: 'dashboard',
        source: gridId,
        tab: this.currentTab,
        subtab: this.currentSubTab,
        filterVisible: null,
        gsOptions: {
          w: 24,
          h: 12,
        },
      }

      await db.dataGrid.widget.createNew(gridData)

      const columnData = {
        type: 'string',
        gridId,
        displayName: 'Name',
        propertyName: 'name'
      }

      await db.dataGrid.data.addColumn(columnData)

      this.lanesStorage = gridId
      this.$nextTick(() => {
        this.laneCol = 'name'
      })
    },
    async doCreateItemsGrid () {
      const gridId = this.$sanitize(this.itemsStorageTitle)
      const gridData = {
        title: this.itemsStorageTitle,
        storageType: 'dashboard',
        source: gridId,
        tab: this.currentTab,
        subtab: this.currentSubTab,
        filterVisible: null,
        gsOptions: {
          w: 24,
          h: 12,
        },
      }

      await db.dataGrid.widget.createNew(gridData)

      const cols = [
        {
          type: 'string',
          gridId,
          displayName: 'Name',
          propertyName: 'name'
        },
        {
          type: 'lookup',
          gridId,
          displayName: 'State',
          propertyName: 'state',
          lookUpTable: this.$sanitize(this.lanesStorageTitle)
        },
        {
          type: 'multiline_text',
          gridId,
          displayName: 'Description',
          propertyName: 'description'
        },
        {
          type: 'string',
          gridId,
          displayName: 'Order',
          propertyName: 'order'
        }
      ]

      for (let columnData of cols) {
        await db.dataGrid.data.addColumn(columnData)
      }

      this.itemsStorage = gridId
      this.$nextTick(() => {
        this.titleCol = 'name'
        this.statusCol = 'state'
        this.descriptionCol = 'description'
        this.sortIndexCol = 'order'
      })
    },
    async save () {
      if (this.inProcess) return
      this.$v.$touch()
      if (this.$v.$invalid) return
      this.inProcess = true

      this.$v.$reset()

      if (this.createLanesGrid) await this.doCreateLanesGrid()
      if (this.createItemsGrid) await this.doCreateItemsGrid()

      const data = {
        tab: this.currentTab,
        subtab: this.currentSubTab,
        lanesStorage: this.lanesStorage,
        laneCol: this.laneCol,
        itemsStorage: this.itemsStorage,
        titleCol: this.titleCol,
        statusCol: this.statusCol,
        descriptionCol: this.descriptionCol,
        sortIndexCol: this.sortIndexCol,
        template: this.template,
        workflow:null,
        gsOptions: {
          w: 24,
          h: 10,
        }
      }
      if (this.addOnChangeScript) data.onChangeScript = this.onChangeScript

      this.$store.commit('addProcess', {
        process: 'newKanban',
        message: 'Creating'
      })

      db.kanban.create(data, (key) => {
        this.$store.commit('addedWidgetKey', key);
        this.lanesStorage = null
        this.laneCol = null
        this.lanesStorageTitle = null
        this.createLanesGrid = false
        this.itemsStorage = null
        this.createItemsGrid = false
        this.titleCol = null
        this.statusCol = null
        this.descriptionCol = null
        this.sortIndexCol = null
        this.workflow = null
        this.template = ''
        this.onChangeScript = ''
        this.$store.commit('deleteProcess', 'newKanban');
        this.inProcess = false
        this.close()
      })
    }
  },
  validations: {
    // lanes
    laneCol: {
      required: requiredUnless('isLanesGridRequired')
    },
    lanesStorage: {
      required: requiredUnless('isLanesGridRequired')
    },
    lanesStorageTitle: {
      required: requiredIf('isLanesGridRequired'),
      isUnique () {
        if (!this.createLanesGrid) return true
        if (!this.lanesStorageTitle) return true
        if (this.$sanitize(this.lanesStorageTitle || '') === this.$sanitize(this.itemsStorageTitle || '')) return false
        if (!this.dataGrid_options) return true
        const grids = Object.keys(this.dataGrid_options)
        if (!grids.includes(this.$sanitize(this.lanesStorageTitle))) return true
        return false
      },
      alphaNum
    },
    // items
    itemsStorage: {
      required: requiredUnless('isItemsGridRequired')
    },
    titleCol: {
      required: requiredUnless('isItemsGridRequired')
    },
    statusCol: {
      required: requiredUnless('isItemsGridRequired')
    },
    descriptionCol: {
      required: requiredUnless('isItemsGridRequired')
    },
    sortIndexCol: {
      required: requiredUnless('isItemsGridRequired')
    },
    itemsStorageTitle: {
      required: requiredIf('isItemsGridRequired'),
      isUnique () {
        if (!this.createItemsGrid) return true
        if (!this.itemsStorageTitle) return true
        if (this.$sanitize(this.lanesStorageTitle || '') === this.$sanitize(this.itemsStorageTitle || '')) return false
        if (!this.dataGrid_options) return true
        const grids = Object.keys(this.dataGrid_options)
        if (!grids.includes(this.$sanitize(this.itmesStorageTitle))) return true
        return false
      },
      alphaNum
    }
  },
  created() {
    eventBus.$on('addKanbanData', () => {
      this.open()
    })
  },
  beforeDestroy() {
    eventBus.$off('addKanbanData')
  }
}
</script>
